import React, { useEffect, useMemo, useState } from "react";
import {
  getTreatmentOptionsByCategory,
  MEDICATION_DEFAULTS,
} from "./treatmentSchemas";
import {
  createBlankTreatment,
  getDefaultTreatmentType,
  getTreatmentSchema,
  normalizeTreatmentForEdit,
} from "./treatmentUtils";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 3000,
};

const modalStyle = {
  width: "100%",
  maxWidth: 980,
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
};

const sectionStyle = {
  padding: 16,
  borderBottom: "1px solid #e5e7eb",
};

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 90,
  resize: "vertical",
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 14,
  color: "#334155",
};

export default function TreatmentModal({
  isOpen,
  initialTreatment,
  onClose,
  onSave,
  employeeOptions = [],
}) {
  const [form, setForm] = useState(createBlankTreatment());

  useEffect(() => {
    if (!isOpen) return;
    setForm(normalizeTreatmentForEdit(initialTreatment));
  }, [isOpen, initialTreatment]);

  const selectedCategory = form.category || "Procedure";

  const treatmentOptions = useMemo(
    () => getTreatmentOptionsByCategory(selectedCategory),
    [selectedCategory]
  );

  const schema = useMemo(() => getTreatmentSchema(form.type), [form.type]);

  if (!isOpen) return null;

  function setField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function setDetailField(field, value) {
    setForm((prev) => ({
      ...prev,
      details: {
        ...(prev.details || {}),
        [field]: value,
      },
    }));
  }

  function handleTypeChange(nextType) {
    const nextBlank = createBlankTreatment(nextType);

    setForm((prev) => ({
      ...nextBlank,
      id: prev.id || nextBlank.id,
      timestamp: prev.timestamp || "",
      performedBy: prev.performedBy || "",
      successful: prev.successful || "",
      notes: prev.notes || "",
    }));
  }

  function handleMedicationChange(medicationName) {
  const defaults = MEDICATION_DEFAULTS[medicationName];

  setForm((prev) => ({
    ...prev,
    details: {
      ...(prev.details || {}),
      medication: medicationName,
      dose: defaults?.dose || prev.details?.dose || "",
      route: defaults?.routes?.[0] || prev.details?.route || "",
      indication: defaults?.indications?.[0] || prev.details?.indication || "",
    },
  }));
}

  function handleSave() {
    onSave?.(normalizeTreatmentForEdit(form));
  }

  function renderDetailField(field) {
    if (field.showWhen) {
      const controllingValue = form.details?.[field.showWhen.field];

      const shouldShow = Array.isArray(controllingValue)
        ? controllingValue.includes(field.showWhen.includes)
        : controllingValue === field.showWhen.equals;

      if (!shouldShow) return null;
    }

    const value = form.details?.[field.name] || "";

    if (field.type === "searchSelect") {
  const listId = `${field.name}-options`;

  return (
    <label key={field.name} style={labelStyle}>
      {field.label}

      <input
        list={listId}
        style={inputStyle}
        value={value}
        placeholder={field.placeholder || ""}
        onChange={(e) => {
  if (field.name === "medication") {
    handleMedicationChange(e.target.value);
  } else {
    setDetailField(field.name, e.target.value);
  }
}}
      />

      <datalist id={listId}>
        {(field.options || []).map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </label>
  );
}

    if (field.type === "multiSelect") {
      const selectedValues = Array.isArray(value)
        ? value
        : String(value || "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);

      function toggleOption(option) {
        const nextValues = selectedValues.includes(option)
          ? selectedValues.filter((item) => item !== option)
          : [...selectedValues, option];

        setDetailField(field.name, nextValues);
      }

      return (
        <div key={field.name} style={labelStyle}>
          {field.label}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            {(field.options || []).map((option) => {
              const active = selectedValues.includes(option);

              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleOption(option)}
                  style={{
                    border: active
                      ? "2px solid #2563eb"
                      : "1px solid #cbd5e1",
                    background: active ? "#dbeafe" : "#fff",
                    color: active ? "#1d4ed8" : "#0f172a",
                    borderRadius: 999,
                    padding: "8px 12px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

                if (field.type === "select") {
                  return (
                    <label key={field.name} style={labelStyle}>
                      {field.label}
                      <select
                        style={inputStyle}
                        value={value}
                      onChange={(e) => {
              if (field.name === "medication") {
                handleMedicationChange(e.target.value);
              } else {
                setDetailField(field.name, e.target.value);
              }
            }}
             >
            {(field.options || []).map((option) => (
              <option key={option || "blank"} value={option}>
                {option || "Select"}
              </option>
            ))}
          </select>
        </label>
      );
    }

    return (
      <label key={field.name} style={labelStyle}>
        {field.label}

        <div style={{ position: "relative" }}>
          <input
            style={{
              ...inputStyle,
              paddingRight: field.suffix ? 56 : 12,
            }}
            value={value}
            placeholder={field.placeholder || ""}
            onChange={(e) => setDetailField(field.name, e.target.value)}
          />

          {field.suffix && (
            <span
              style={{
                position: "absolute",
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                fontWeight: 700,
                pointerEvents: "none",
              }}
            >
              {field.suffix}
            </span>
          )}
        </div>
      </label>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            ...sectionStyle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>
             {initialTreatment?.id
  ? `Edit ${selectedCategory}`
  : `Add ${selectedCategory}`}
            </div>
          </div>

          <button type="button" style={buttonStyle} onClick={onClose}>
            Close
          </button>
        </div>

        <div style={sectionStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
            }}
          >

            <label style={labelStyle}>
              Type
              <select
                style={inputStyle}
                value={form.type}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                {treatmentOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              Timestamp
              <input
                style={inputStyle}
                type="datetime-local"
                value={form.timestamp}
                onChange={(e) => setField("timestamp", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Performed By
              <input
                list="report-completed-options"
                style={inputStyle}
                value={form.performedBy}
                onChange={(e) => setField("performedBy", e.target.value)}
                placeholder="Name, Level"
              />
            </label>

            <label style={labelStyle}>
              Successful
              <select
                style={inputStyle}
                value={form.successful}
                onChange={(e) => setField("successful", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>
            {schema.label} Details
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
            }}
          >
            {(schema.detailFields || []).map(renderDetailField)}
          </div>
        </div>

        <div style={sectionStyle}>
          <label style={labelStyle}>
            Notes
            <textarea
              style={textareaStyle}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />
          </label>
        </div>

        <div
          style={{
            ...sectionStyle,
            borderBottom: "none",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button type="button" style={buttonStyle} onClick={onClose}>
            Cancel
          </button>
          <button type="button" style={primaryButtonStyle} onClick={handleSave}>
            Save Treatment
          </button>
        </div>
      </div>
    </div>
  );
}